import type { NavigateFunction } from 'react-router-dom';
import type { ToolHandoffOrigin, ForgeWorkOrigin } from '@shared/types/continuations';

const FLOW_COMPOSER_ROUTE = '/forge';
const FLOW_COMPOSER_PROMPT_STORAGE_PREFIX = 'domino:flow-composer:prompt:';

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const createPromptKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const storePrompt = (prompt: string): string | null => {
  try {
    const key = createPromptKey();
    window.sessionStorage.setItem(`${FLOW_COMPOSER_PROMPT_STORAGE_PREFIX}${key}`, prompt);
    return key;
  } catch {
    return null;
  }
};

const readStoredPrompt = (key: string): string | null => {
  try {
    const storageKey = `${FLOW_COMPOSER_PROMPT_STORAGE_PREFIX}${key}`;
    const value = window.sessionStorage.getItem(storageKey);
    const trimmed = value?.trim() ?? '';
    return trimmed || null;
  } catch {
    return null;
  }
};

export const navigateToFlowComposerPrompt = (
  navigate: NavigateFunction,
  prompt: string,
  origin?: ToolHandoffOrigin | ForgeWorkOrigin | null,
): void => {
  const trimmed = prompt.trim();
  if (!trimmed) return;

  const promptKey = storePrompt(trimmed);
  if (promptKey) {
    navigate(FLOW_COMPOSER_ROUTE, {
      state: {
        initialPromptKey: promptKey,
        ...(origin ? { threadActionOrigin: origin } : {}),
      },
    });
    return;
  }

  navigate(FLOW_COMPOSER_ROUTE, {
    state: {
      initialPrompt: trimmed,
      ...(origin ? { threadActionOrigin: origin } : {}),
    },
  });
};

export const resolveFlowComposerThreadActionOrigin = (
  locationState: unknown,
): ForgeWorkOrigin | null => {
  if (!isRecord(locationState)) return null;
  const origin = locationState.threadActionOrigin;
  if (!isRecord(origin)) return null;
  if (typeof origin.proposalId === 'string' && typeof origin.topicId === 'string' && typeof origin.expectedVersion === 'number' && Number.isInteger(origin.expectedVersion) && origin.expectedVersion > 0) return {proposalId:origin.proposalId,topicId:origin.topicId,expectedVersion:origin.expectedVersion};
  const sourceRunId = typeof origin.sourceRunId === 'string'
    ? origin.sourceRunId.trim()
    : '';
  const sourceActionId = typeof origin.sourceActionId === 'string'
    ? origin.sourceActionId.trim()
    : '';
  return sourceRunId && sourceActionId ? { sourceRunId, sourceActionId } : null;
};

export const buildResultToolCreationPrompt = (seedPrompt: string): string => {
  const trimmed = seedPrompt.trim();
  if (!trimmed) return '';

  return [
    'Je veux créer un tool Domino réutilisable à partir de ce besoin métier.',
    '',
    'Besoin à couvrir :',
    trimmed,
    '',
    'Attendu de la Forge :',
    "- transforme ce besoin en tool générique, pas en outil figé sur l'exemple du run actuel ;",
    '- fais couvrir le cas précis par 2 ou 3 inputs maximum, simples et métier ;',
    "- choisis des inputs réutilisables seulement s'ils sont nécessaires, par exemple : compte ou sujet, URL ou contexte court, objectif ou angle ;",
    '- ne code pas les valeurs du contexte en dur : utilise-les comme exemple de résultat attendu ;',
    '- produis une sortie claire pour un utilisateur métier, avec synthèse, raisons ou preuves et prochaines actions si pertinent.',
    '',
    'Commence par proposer le cadrage et les inputs avant de construire le tool.',
  ].join('\n');
};

export const resolveInitialFlowComposerPrompt = (
  searchParams: URLSearchParams,
  locationState: unknown,
): string | null => {
  const queryPrompt = searchParams.get('prompt')?.trim() ?? '';
  if (queryPrompt) return queryPrompt;

  const promptKey = searchParams.get('promptKey')?.trim() ?? '';
  if (promptKey) {
    const storedPrompt = readStoredPrompt(promptKey);
    if (storedPrompt) return storedPrompt;
  }

  if (isRecord(locationState)) {
    if (typeof locationState.initialPromptKey === 'string') {
      const storedPrompt = readStoredPrompt(locationState.initialPromptKey.trim());
      if (storedPrompt) return storedPrompt;
    }

    if (typeof locationState.initialPrompt !== 'string') return null;

    const statePrompt = locationState.initialPrompt.trim();
    if (statePrompt) return statePrompt;
  }

  return null;
};
